import Image from 'next/image'
import {FC, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ModalWrapper} from '@/components/common/modal/ModalWrapper'
import {useAuthContent} from '@/content/user/authentication'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {useRouter} from '@/utils/hooks/useRouter'
import {routeTo} from '@/utils/router'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {AuthActionType, AuthPageType} from '@/utils/user/constants'

import {AuthModal} from './AuthModal'

export const SignIn: FC = () => {
  const {t} = useTranslation()
  const {mandatory, signUpLinkText, buttonText} = useAuthContent().signIn
  const [authActionType, setAuthActionType] = useState<AuthActionType | null>(null)

  const router = useRouter()

  const closeModal = () => setAuthActionType(null)
  const onSuccessfulSignIn = () => {
    router.push(routeTo.portal.trademark.list())
  }

  const type = Object.values(AuthPageType).includes(router.query.type as AuthPageType)
    ? (router.query.type as AuthPageType)
    : AuthPageType.general
  const content = mandatory.content[type]

  return (
    <>
      <div className="wrapper">
        <div className="image">
          <Image
            src={mandatory.imageSrc}
            alt={mandatory.imageAlt}
            fill
            style={{objectFit: 'contain'}}
            sizes="64px"
          />
        </div>
        <Text variant={TextVariant.h4} element="h2">
          {t(content.title)}
        </Text>
        <div className="description">
          <Text fontWeight={TextFontWeight.medium} color={theme.palette.text.light}>
            {t(content.description)}
          </Text>
        </div>
        <div className="button-wrapper">
          <Button
            type="button"
            fullWidth
            variant={ButtonVariant.primary}
            size={ButtonSize.small}
            textVariant={TextVariant.subtitle13}
            onClick={() => setAuthActionType(AuthActionType.signIn)}
            data-test="LoginButton"
          >
            {t(buttonText)}
          </Button>
        </div>
        <div className="button-wrapper">
          <Button
            type="button"
            fullWidth
            variant={ButtonVariant.tertiary}
            size={ButtonSize.small}
            textVariant={TextVariant.subtitle13}
            onClick={() => setAuthActionType(AuthActionType.signUp)}
            data-test="SignIn-signUpButton"
          >
            {t(signUpLinkText)}
          </Button>
        </div>
      </div>
      <ModalWrapper
        isVisible={Boolean(authActionType)}
        props={authActionType ? {authActionType, closeModal, onSuccessfulSignIn} : null}
      >
        {(props) => <AuthModal {...props} />}
      </ModalWrapper>

      <style jsx>{`
        .wrapper {
          max-width: 540px;
          margin: 0 auto;
          text-align: center;
        }
        .image {
          position: relative;
          width: 64px;
          height: 64px;
          margin: 0 auto ${theme.spacing[3]};
        }
        .description {
          margin-top: ${theme.spacing[2]};
        }
        .button-wrapper {
          max-width: 312px;
          margin: ${theme.spacing[4]} auto 0;
        }
        .button-wrapper + .button-wrapper {
          margin-top: ${theme.spacing[2]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .button-wrapper {
            max-width: inherit;
          }
        }
      `}</style>
    </>
  )
}
